import i18n from "i18next";
import ChainedBackend from "i18next-chained-backend";
import HttpBackend from "i18next-http-backend";
import LocalStorageBackend from "i18next-localstorage-backend";
import { initReactI18next } from "react-i18next";
import { z } from "zod";
import api from "./lib/axiosConfig";
import { getActiveRegion } from "./config/regions";
import { queryClient } from "./lib/queryClient";
import { authFallbackResources } from "./locales/auth-fallback";
import { printMultiplesFallbackResources } from "./locales/print-multiples-fallback";

export const supportedLocales = ["fr", "en", "es", "nl"] as const;

export const multilingualFieldSchema = <T extends z.ZodType>(type: T) =>
  z.record(z.enum(supportedLocales), type.optional());

// equivalent to multilingualFieldSchema
export type LocaleDict<T = string> = {
  [locale in SupportedLocale]?: T;
};

export const constructLocaleDict = <T>(
  func: (lang: SupportedLocale) => T | undefined,
): LocaleDict<T> =>
  supportedLocales.reduce((acc, lang) => ({ ...acc, [lang]: func(lang) }), {});

export const constructCmsLocaleDict = <T>(
  func: (lang: SupportedLocale) => T | undefined,
  locales: readonly SupportedLocale[] = supportedLocales,
): LocaleDict<T> =>
  locales.reduce(
    (acc, lang) => ({ ...acc, [lang]: func(lang) }),
    {} as LocaleDict<T>,
  );

let mainLanguage: SupportedLocale;

export const getMainLanguage = () =>
  mainLanguage ?? localStorage.getItem("main-language") ?? "en";

export const setMainLanguage = (lang: SupportedLocale) => {
  mainLanguage = lang;
  localStorage.setItem("main-language", lang);
  i18n.changeLanguage(lang);
};

export async function fetchMainLanguage() {
  const { data } = await api.get("back/api/instance/current");
  setMainLanguage(data.mainLanguage);
  await queryClient.invalidateQueries({
    queryKey: ["cms", "general-settings"],
  });
}

export type SupportedLocale = (typeof supportedLocales)[number];

export const getDecimalSeparator = (locale: SupportedLocale) => {
  const numberWithDecimalSeparator = 1.1;
  const part = Intl.NumberFormat(locale)
    .formatToParts(numberWithDecimalSeparator)
    .find((part) => part.type === "decimal");
  return part?.value || ".";
};

export const getThousandSeparator = (locale: SupportedLocale) => {
  const numberWithThousandSeparator = 100000;
  const part = Intl.NumberFormat(locale)
    .formatToParts(numberWithThousandSeparator)
    .find((part) => part.type === "group");
  return part?.value || ",";
};

export const isSupportedLocale = (locale: string): locale is SupportedLocale =>
  (supportedLocales as unknown as string[]).includes(locale);

/** Map i18next language (e.g. fr-FR) to a SupportedLocale for API / override maps. */
export function toSupportedLocale(
  lang: string | undefined | null,
): SupportedLocale {
  if (!lang) return "fr";
  const lower = lang.toLowerCase();
  if (isSupportedLocale(lower)) return lower;
  const base = lower.split("-")[0] ?? "";
  if (isSupportedLocale(base)) return base;
  return "fr";
}

/** Default i18next namespace used by react-i18next in this app */
export const I18N_DEFAULT_NS = "translation";

/**
 * Namespace de secours (embarqué) pour les libellés des pages d'auth. Sert de
 * `fallbackNS` : jamais chargé depuis le backend, uniquement amorcé en mémoire.
 */
export const AUTH_FALLBACK_NS = "auth";

/**
 * Namespace de secours (embarqué) pour les libellés du module d'impression des
 * estampes multiples (`bo.print.mp.*`). Même principe que `auth` : jamais chargé
 * depuis le backend, sert de repli via `fallbackNS`.
 */
export const PRINT_MULTIPLES_FALLBACK_NS = "print-multiples";

const localStorageTranslationKey = (lng: SupportedLocale) =>
  `i18next_res_${lng}-${I18N_DEFAULT_NS}`;

/**
 * Clears i18next-localstorage-backend entries so the chained backend falls
 * through to HTTP and fetches fresh global + instance merged translations.
 */
export function clearTranslationLocalStorageCache(
  languages: readonly SupportedLocale[] = supportedLocales,
) {
  if (typeof window === "undefined" || !window.localStorage) return;
  for (const lng of languages) {
    window.localStorage.removeItem(localStorageTranslationKey(lng));
  }
}

/**
 * After custom labels (I18n instance) or global strings change, bust the
 * local cache and reload all locale bundles from the API.
 */
export async function reloadAppTranslationResources(
  languages: readonly SupportedLocale[] = supportedLocales,
) {
  clearTranslationLocalStorageCache(languages);
  await i18n.reloadResources([...languages], I18N_DEFAULT_NS);
  await i18n.changeLanguage(i18n.language);
}

function translationHttpHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  try {
    const token = localStorage.getItem("token");
    const instance = localStorage.getItem("instance");
    if (token) headers.Authorization = `Bearer ${token}`;
    if (instance) headers.Instance = instance;
  } catch {
    // ignore
  }
  return headers;
}

i18n
  .use(ChainedBackend)
  .use(initReactI18next)
  .init({
    lng: getMainLanguage(),
    supportedLngs: supportedLocales,
    // Namespace de secours contenant les libellés d'auth embarqués. Le namespace
    // principal (`translation`) se charge normalement depuis le backend ; tant
    // qu'il n'est pas arrivé, `t()` retombe sur `auth` (embarqué) → page de login
    // traduite dès le premier rendu, sans attendre le bundle distant.
    fallbackNS: [AUTH_FALLBACK_NS, PRINT_MULTIPLES_FALLBACK_NS],
    detection: {
      order: ["localStorage"],
      caches: ["localStorage"],
    },
    backend: {
      backends: [LocalStorageBackend, HttpBackend],
      // La copie localStorage s'affiche tout de suite (jamais de clés brutes à
      // l'écran pendant le chargement) ; le bundle est ensuite redemandé à l'API
      // en arrière-plan à CHAQUE chargement de page, puis remplace celui affiché
      // et la copie locale. Une trad modifiée dans galadmin apparaît donc en un
      // ou deux rechargements (le cache global de l'API vit 10 s), sans jamais
      // bloquer l'affichage.
      cacheHitMode: "refreshAndUpdateStore",
      backendOptions: [
        {
          // Durée de vie de la copie locale. Longue exprès : la fraîcheur vient
          // du rafraîchissement en arrière-plan, pas de l'expiration. Une copie
          // expirée n'est plus affichée, et on retomberait sur les clés brutes
          // pendant le téléchargement (~300 Ko).
          expirationTime: 7 * 24 * 60 * 60 * 1000, // 7 jours
        },
        {
          loadPath: (lngs: string[]) =>
            `${getActiveRegion().baseUrl}back/api/translations/${lngs[0]}/app/app-formatted`,
          customHeaders: translationHttpHeaders,
          requestOptions: {
            // no-cache (et non no-store) : le navigateur peut revalider via
            // l'ETag renvoyé par l'API (304) au lieu de re-télécharger le bundle
            // entier, tout en ne servant jamais de contenu périmé sans contrôle.
            cache: "no-cache" as RequestCache,
          },
        },
      ],
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
      // Re-rend les composants quand le rafraîchissement en arrière-plan
      // (`cacheHitMode` ci-dessus) remplace le bundle affiché.
      bindI18nStore: "added",
    },
  });

// Amorçage du namespace de secours `auth` avec les libellés embarqués (toutes
// langues). Ce namespace n'est jamais chargé depuis le backend (il n'est pas dans
// `ns`), donc il n'interfère pas avec le chargement normal du namespace principal
// `translation`. Il sert uniquement de repli instantané via `fallbackNS`.
for (const lng of supportedLocales) {
  i18n.addResourceBundle(
    lng,
    AUTH_FALLBACK_NS,
    authFallbackResources[lng],
    true,
    false,
  );
  i18n.addResourceBundle(
    lng,
    PRINT_MULTIPLES_FALLBACK_NS,
    printMultiplesFallbackResources[lng],
    true,
    false,
  );
}

i18n.on("languageChanged", (lng) => {
  localStorage.setItem("language", lng);
});

export default i18n;
